// Connection context for managing selected database connection
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { DatabaseConnection, TokenManager } from '@/utils/api';
import { RootState } from '@/app/store';

interface ConnectionContextType {
  selectedConnection: DatabaseConnection | null;
  setSelectedConnection: (connection: DatabaseConnection | null) => void;
  clearConnection: () => void;
  /** True once "is there a saved connection at all" is known — always true
   * from the first render (the localStorage read below is synchronous), but
   * kept as an explicit field rather than inlined `true` so consumers like
   * `ProtectedDesignerRoute` have one honest thing to check instead of
   * assuming synchronous timing (Task 9 fix-round-2, ERP Users epic). */
  hydrated: boolean;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(undefined);

export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error('useConnection must be used within ConnectionProvider');
  }
  return context;
};

interface ConnectionProviderProps {
  children: ReactNode;
}

function storageKey(userId: string): string {
  return `selectedConnection:${userId}`;
}

function readStoredConnection(userId: string | null): DatabaseConnection | null {
  if (!userId) return null;
  const key = storageKey(userId);
  const saved = localStorage.getItem(key);
  if (!saved) return null;
  try {
    return JSON.parse(saved) as DatabaseConnection;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function writeStoredConnection(userId: string | null, connection: DatabaseConnection | null) {
  if (!userId) return;
  const key = storageKey(userId);
  if (connection) {
    localStorage.setItem(key, JSON.stringify(connection));
  } else {
    localStorage.removeItem(key);
  }
}

export const ConnectionProvider: React.FC<ConnectionProviderProps> = ({ children }) => {
  const userId = useSelector((state: RootState) => state.auth.user?.id ?? null);
  const [selectedConnection, setSelectedConnectionState] = useState<DatabaseConnection | null>(
    () => readStoredConnection(userId),
  );
  const hydrated = true;
  const [seenUserId, setSeenUserId] = useState(userId);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const selectionIdRef = useRef<string | null>(selectedConnection?.id ?? null);
  selectionIdRef.current = selectedConnection?.id ?? null;
  if (seenUserId !== userId) {
    setSeenUserId(userId);
    setSelectedConnectionState(readStoredConnection(userId));
  }

  useEffect(() => {
    const token = TokenManager.getAccessToken();
    const stored = readStoredConnection(userId);
    const id = stored?.id;
    const keyUserId = userId;
    if (!token || !id || !keyUserId) return;
    let active = true;
    const stillCurrent = () => active && userIdRef.current === keyUserId;
    const requestedId = id;
    const selectionStillRequested = () =>
      stillCurrent()
      && selectionIdRef.current === requestedId
      && readStoredConnection(keyUserId)?.id === requestedId;
    fetch(`/api/connections/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!selectionStillRequested()) return null;
        if (r.ok) return r.json();
        if (r.status === 404) {
          setSelectedConnectionState(null);
          localStorage.removeItem(storageKey(keyUserId));
        }
        return null;
      })
      .then(fresh => {
        if (!selectionStillRequested() || !fresh) return;
        setSelectedConnectionState(fresh);
        localStorage.setItem(storageKey(keyUserId), JSON.stringify(fresh));
      })
      .catch(() => { /* keep cached version */ });
    return () => {
      active = false;
    };
  }, [userId]);

  const setSelectedConnection = (connection: DatabaseConnection | null) => {
    setSelectedConnectionState(connection);
    writeStoredConnection(userId, connection);
  };

  const clearConnection = () => {
    setSelectedConnectionState(null);
    writeStoredConnection(userId, null);
  };

  return (
    <ConnectionContext.Provider
      value={{
        selectedConnection,
        setSelectedConnection,
        clearConnection,
        hydrated,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  );
};
